import React from "react";
import { Star, MessageSquare, ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";

// Google Maps place URL for Autó-Szerelem Build & Service Kft.
// Opens the business profile page where the "Write a review" button is
// prominently visible. Uses the stable CID (Customer ID) format.
const GOOGLE_REVIEW_URL =
  "https://maps.google.com/?cid=5114961728345414404";

// Inline Google G logo — used in header + attribution
const GoogleG = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

// Star rating row — 5 stars with partial fill for the average
function StarRow({ value, size = "w-4 h-4" }) {
  const rounded = Math.round((value ?? 0) * 2) / 2; // nearest half
  return (
    <div className="flex items-center gap-0.5" aria-label={`${value ?? 0} / 5 csillag`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} text-[#FBBC05]`}
          strokeWidth={1.5}
          fill={n <= rounded ? "#FBBC05" : "none"}
        />
      ))}
    </div>
  );
}

// Truncate long review text
function truncate(text, max = 260) {
  if (!text || text.length <= max) return text || "";
  return text.slice(0, max).trimEnd() + "…";
}

// One real Google review card
function ReviewCard({ review, testId }) {
  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 p-5 sm:p-6 hover:border-[#0082D2]/40 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          {review.author_photo ? (
            <img
              src={review.author_photo}
              alt={review.author_name || "Google felhasználó"}
              width={40}
              height={40}
              referrerPolicy="no-referrer"
              className="w-10 h-10 rounded-full object-cover border border-white/10 shrink-0"
              loading="lazy"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center bg-[#0082D2]/15 border border-[#0082D2]/30 text-[#0082D2] font-bold shrink-0"
              style={{ fontFamily: "Barlow Condensed" }}
              aria-hidden="true"
            >
              {(review.author_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            {review.author_url ? (
              <a
                href={review.author_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-white font-semibold text-sm hover:text-[#0082D2] transition-colors truncate"
              >
                {review.author_name || "Google felhasználó"}
              </a>
            ) : (
              <div className="text-white font-semibold text-sm truncate">
                {review.author_name || "Google felhasználó"}
              </div>
            )}
            <div className="text-[0.65rem] uppercase tracking-[0.18em] text-zinc-500 mt-0.5">
              {review.relative_time || "Google értékelés"}
            </div>
          </div>
        </div>
        <StarRow value={review.rating} />
      </div>

      {review.text ? (
        <p className="text-sm text-zinc-300 leading-relaxed">{truncate(review.text)}</p>
      ) : (
        <p className="text-sm text-zinc-500 italic">Csillag értékelés szöveg nélkül.</p>
      )}

      {review.review_url && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.2em] text-zinc-500">
            <GoogleG className="w-3.5 h-3.5" /> Google
          </span>
          <a
            href={review.review_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-[#0082D2] hover:text-white transition-colors inline-flex items-center gap-1"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            Megnyitás <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </div>
  );
}

// Skeleton loading card
function SkeletonCard() {
  return (
    <div className="bg-[#0A0A0A]/60 border border-white/10 p-5 animate-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-white/10" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-white/10 rounded w-1/2" />
          <div className="h-2 bg-white/5 rounded w-1/3" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-2 bg-white/5 rounded w-full" />
        <div className="h-2 bg-white/5 rounded w-11/12" />
        <div className="h-2 bg-white/5 rounded w-4/5" />
      </div>
    </div>
  );
}

// When Google API returns a rating (star + count) but no review body yet
// (common for fresh reviews within the first few hours of being submitted)
function RatingWithoutTextBlock({ rating, count, googleMapsUri }) {
  return (
    <>
      <div
        className="relative bg-[#0A0A0A] border border-[#FBBC05]/40 p-6 sm:p-7 overflow-hidden"
        data-testid="reviews-fresh-rating"
        style={{
          background:
            "linear-gradient(135deg, rgba(251,188,5,0.10) 0%, #0A0A0A 60%)",
        }}
      >
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center bg-[#FBBC05]/15 border border-[#FBBC05]/40"
              aria-hidden="true"
            >
              <Star className="w-6 h-6 text-[#FBBC05]" strokeWidth={1.5} fill="#FBBC05" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-semibold">
                Első értékelésünk megérkezett!
              </div>
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                {count} értékelés · {rating != null ? rating.toFixed(1) : "?"} csillag
              </div>
            </div>
          </div>
          <StarRow value={rating || 0} size="w-5 h-5" />
        </div>

        <p className="text-sm text-zinc-300 leading-relaxed">
          Köszönjük, hogy időt szántál rá! A Google pár óra alatt dolgozza fel
          az új értékeléseket — hamarosan a szöveg is itt fog megjelenni,
          szerző névvel és avatárral együtt.
        </p>

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.2em] text-zinc-500">
            <GoogleG className="w-3.5 h-3.5" /> Google
          </span>
          <a
            href={googleMapsUri}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-[#0082D2] hover:text-white transition-colors inline-flex items-center gap-1"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            Megnyitás Google-on <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="bg-[#0A0A0A]/60 border border-dashed border-white/10 p-5 hover:border-[#0082D2]/40 transition-colors"
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-zinc-500 font-bold"
                style={{ fontFamily: "Barlow Condensed" }}
                aria-hidden="true"
              >
                +
              </div>
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((k) => (
                  <Star
                    key={k}
                    className="w-3.5 h-3.5 text-zinc-700"
                    strokeWidth={1.5}
                    fill="none"
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-2 bg-white/5 rounded w-full" />
              <div className="h-2 bg-white/5 rounded w-11/12" />
              <div className="h-2 bg-white/5 rounded w-3/4" />
            </div>
            <p className="mt-4 text-[0.7rem] text-zinc-600 italic">
              A következő vélemény ide kerül
            </p>
          </div>
        ))}
      </div>

      <p className="text-[0.7rem] text-zinc-600 leading-relaxed">
        Ahogy megérkeznek a további Google-értékelések, ez a szekció
        automatikusan bővül. Segíts te is — ha nálunk jártál, ossz meg pár
        mondatot Debrecen sokat segít 🙏
      </p>
    </>
  );
}

// Placeholder invitation when there are no reviews yet
function InvitationBlock({ googleMapsUri }) {
  const writeUrl = googleMapsUri || GOOGLE_REVIEW_URL;
  return (
    <>
      <div
        className="relative bg-[#0A0A0A] border border-[#0082D2]/40 p-6 sm:p-7 overflow-hidden"
        data-testid="reviews-first-invitation"
        style={{
          background:
            "linear-gradient(135deg, rgba(0,130,210,0.10) 0%, #0A0A0A 60%)",
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center bg-[#0082D2]/15 border border-[#0082D2]/40 text-[#0082D2] font-black text-lg"
              style={{ fontFamily: "Barlow Condensed" }}
              aria-hidden="true"
            >
              Ö
            </div>
            <div className="leading-tight">
              <div className="text-white font-semibold">
                Legyen Ön az első véleményező
              </div>
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Az Ön neve itt fog megjelenni
              </div>
            </div>
          </div>
          <StarRow value={5} />
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed italic">
          „Az első értékelés itt fog megjelenni. Egy néhány mondatos
          visszajelzés a klímatöltésről, az automataváltó-olajcseréről vagy
          bármelyik szolgáltatásunkról óriási segítség — másoknak is Önhöz
          köszönhetően találjuk meg őket.”
        </p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
            Google · Debrecen
          </span>
          <a
            href={writeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-[#0082D2] hover:text-white transition-colors inline-flex items-center gap-1"
            data-testid="reviews-inline-cta"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            Elkezdem <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="reviews-placeholder-grid">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="bg-[#0A0A0A]/60 border border-dashed border-white/10 p-5 hover:border-[#0082D2]/40 transition-colors"
            data-testid={`reviews-placeholder-${i}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-zinc-500 font-bold"
                style={{ fontFamily: "Barlow Condensed" }}
                aria-hidden="true"
              >
                ?
              </div>
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((k) => (
                  <Star
                    key={k}
                    className="w-3.5 h-3.5 text-zinc-700"
                    strokeWidth={1.5}
                    fill="none"
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-2 bg-white/5 rounded w-full" />
              <div className="h-2 bg-white/5 rounded w-11/12" />
              <div className="h-2 bg-white/5 rounded w-3/4" />
            </div>
            <p className="mt-4 text-[0.7rem] text-zinc-600 italic">
              Az Ön véleménye jöhet ide
            </p>
          </div>
        ))}
      </div>

      <p className="text-[0.7rem] text-zinc-600 leading-relaxed">
        Ahogy megérkeznek az első valós Google-értékelések, ez a szekció
        automatikusan bővül. Minden vélemény segít — köszönjük!
      </p>
    </>
  );
}

export default function Reviews() {
  const [state, setState] = React.useState({ loading: true, data: null });

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/reviews");
        if (alive) setState({ loading: false, data: res.data });
      } catch {
        if (alive) setState({ loading: false, data: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const reviews = state.data?.reviews || [];
  const hasReviews = reviews.length > 0;
  const rating = state.data?.rating;
  const count = state.data?.user_rating_count || 0;
  const hasRating = (rating != null && rating > 0) || count > 0;
  const googleMapsUri = state.data?.google_maps_uri || GOOGLE_REVIEW_URL;

  return (
    <section
      id="velemenyek"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="reviews-section"
      aria-label="Google vélemények"
    >
      {/* Subtle Google-blue accent glow */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full blur-3xl opacity-[0.08]"
          style={{ background: "radial-gradient(circle, #4285F4 0%, transparent 70%)" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="section-eyebrow mb-4">Vélemények</span>
            <h2
              className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
              }}
              data-testid="reviews-heading"
            >
              Mit mondanak
              <br />
              <span className="text-[#0082D2]">Debrecenben rólunk?</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 leading-relaxed">
            {hasReviews
              ? "Valódi ügyfeleink véleményei Google-on. Kattintson bármelyikre, hogy megnyissa az eredeti értékelést."
              : "Épp most építjük a Google-profilunkon a valós ügyfélvéleményeket. Segítsen Ön is — ha nálunk járt, ossza meg tapasztalatát pár mondatban."}
          </p>
        </div>

        {/* Main card */}
        <div
          className="relative bg-[#0d0d0d] border border-white/10 overflow-hidden"
          data-testid="reviews-google-card"
        >
          <div className="m-stripe absolute top-0 left-0 right-0 h-[2px]" />

          <div className="grid lg:grid-cols-12 gap-10 p-8 sm:p-12 lg:p-14">
            {/* LEFT — Google identity + rating summary */}
            <div className="lg:col-span-5 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-3 mb-6" data-testid="reviews-google-logo">
                  <GoogleG className="w-9 h-9" />
                  <div className="flex flex-col leading-tight">
                    <span
                      className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-zinc-500"
                      style={{ fontFamily: "Barlow Condensed" }}
                    >
                      Google értékelés
                    </span>
                    <span className="text-white font-semibold text-sm">
                      Autó-Szerelem — Séra Autószervíz
                    </span>
                  </div>
                </div>

                {/* Rating badge — dynamic */}
                <div className="flex items-center gap-3 mb-4" data-testid="reviews-star-row">
                  {hasRating ? (
                    <>
                      <span
                        className="text-3xl font-black text-white leading-none"
                        style={{ fontFamily: "Barlow Condensed" }}
                      >
                        {rating != null ? rating.toFixed(1) : "—"}
                      </span>
                      <StarRow value={rating || 0} size="w-6 h-6" />
                      <span
                        className="ml-2 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-zinc-400"
                        style={{ fontFamily: "Barlow Condensed" }}
                      >
                        {count} {count === 1 ? "értékelés" : "értékelés"}
                      </span>
                    </>
                  ) : (
                    <>
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Star
                          key={i}
                          className="w-8 h-8 text-[#FBBC05]"
                          strokeWidth={1.5}
                          fill="none"
                        />
                      ))}
                      <span
                        className="ml-2 text-[0.7rem] font-bold uppercase tracking-[0.22em] text-zinc-500"
                        style={{ fontFamily: "Barlow Condensed" }}
                      >
                        Még nincs értékelés
                      </span>
                    </>
                  )}
                </div>

                <p className="text-zinc-400 leading-relaxed max-w-md">
                  {hasReviews
                    ? "Az alábbi értékelések közvetlenül a Google Business Profile-unkról származnak, valós ügyfelektől. Kattintson bármelyikre az eredeti Google értékelés megnyitásához."
                    : hasRating
                      ? "Az első értékelésünk megérkezett! A szöveg pár órán belül itt is megjelenik. Addig is a Google Maps-en már látható."
                      : "Nem hazudunk kitalált véleményekkel. Ha nálunk járt és megelégedéssel távozott, néhány mondatod aranyat ér — segítesz másoknak is minket megtalálni Debrecenben."}
                </p>

                <div className="mt-6 inline-flex items-center gap-2 text-[0.7rem] text-zinc-500">
                  <MapPin className="w-3.5 h-3.5 text-[#0082D2]" strokeWidth={1.6} />
                  <span>4031 Debrecen, Vadvirág utca 15/D — 3. kapu</span>
                </div>
              </div>

              <div className="mt-8 lg:mt-10">
                <a
                  href={googleMapsUri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center"
                  data-testid="reviews-write-cta"
                >
                  <MessageSquare className="w-4 h-4 mr-2" strokeWidth={2} />
                  Vélemény írása a Google-on
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>

            {/* RIGHT — Dynamic: real reviews OR invitation placeholder */}
            <div className="lg:col-span-7 grid gap-4" data-testid="reviews-right-column">
              {state.loading ? (
                <>
                  <SkeletonCard />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <SkeletonCard />
                    <SkeletonCard />
                  </div>
                </>
              ) : hasReviews ? (
                <>
                  {/* Featured first review */}
                  <ReviewCard review={reviews[0]} testId="review-card-0" />
                  {/* Remaining in grid */}
                  {reviews.length > 1 && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {reviews.slice(1).map((r, i) => (
                        <ReviewCard
                          key={i + 1}
                          review={r}
                          testId={`review-card-${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                  {/* Google attribution footer */}
                  <div className="mt-2 flex items-center justify-between gap-3 text-[0.7rem] text-zinc-500">
                    <span className="inline-flex items-center gap-1.5">
                      <GoogleG className="w-3.5 h-3.5" />
                      Vélemények forrása: Google Maps
                    </span>
                    <a
                      href={googleMapsUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-[#0082D2] transition-colors"
                    >
                      Összes megtekintése →
                    </a>
                  </div>
                </>
              ) : hasRating ? (
                <RatingWithoutTextBlock
                  rating={rating}
                  count={count}
                  googleMapsUri={googleMapsUri}
                />
              ) : (
                <InvitationBlock googleMapsUri={googleMapsUri} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
